import React from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { StyledItemRow, StyledItemTextWrapper, StyledCheckboxWrapper, StyledProductImage, styles, StyledAddProductWrapper, StyledTitle } from './styles';
import { Checkbox } from '../Checkbox';
import { ReactComponent as AddProduct } from '@assets/icons/circle-add-filled.svg';
import { Skeleton, SkeletonThumbnail } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';
import { useHistory, useLocation, useRouterHelper } from '@shared/core';
import { routePaths } from '@apps/admin/route.paths';
import { qs } from '@shared/core/queryString';
import ClampedText from '../../../ClampedText';
import { useRegistryChecklistTelemetry } from '../../RegistryChecklist.telemetry';

interface ItemProps {
  itemTitle: string;
  productTitle: Maybe<string>;
  isCompleted: boolean;
  productThumbnailUrl?: string;
  isCheckable: boolean;
  handleOnCheckItem: (checked: boolean) => void;
  ctaUrl: string;
  onCloseDrawer: () => void;
}

export const Item = ({ isCompleted, itemTitle, productTitle, productThumbnailUrl, isCheckable, handleOnCheckItem, ctaUrl, onCloseDrawer }: ItemProps) => {
  const routeHelper = useRouterHelper();
  const location = useLocation();
  const history = useHistory();
  const { onChecklistItemClicked } = useRegistryChecklistTelemetry();

  const params = new URLSearchParams(location.search);
  const hasSearchString = params.get('s')?.length;

  if (hasSearchString) {
    params.delete('s');
  }

  const completeCtaUrl = routeHelper.buildPath(
    qs.stringifyUrl({
      url: `${routePaths.home.path}${ctaUrl.replace('/', '')}`, // Remove leading '/' character from CtaUrl
      query: { ...qs.parse(params.toString()), source: 'checklist' }
    })
  );

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleOnCheckItem(e.currentTarget.checked);
  };

  const handleOnItemClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onChecklistItemClicked({ item: itemTitle });
    history.push(completeCtaUrl);
    onCloseDrawer();
  };

  return (
    <StyledItemRow __css={styles.itemRow}>
      <StyledCheckboxWrapper __css={styles.checkboxWrapper}>
        <Checkbox isChecked={isCompleted} isDisabled={!isCheckable} onChange={handleOnChange} />
      </StyledCheckboxWrapper>
      <Flex alignItems="center" width="100%" as="a" href={completeCtaUrl} onClick={handleOnItemClick} role="group">
        <StyledProductImage __css={styles.productImage(isCompleted)} {...(productThumbnailUrl && { _after: { backgroundImage: `url(${productThumbnailUrl}?w=53&h=53)` } })}>
          {isCompleted && isCheckable ? null : (
            <StyledAddProductWrapper opacity={isCompleted && productThumbnailUrl ? 0 : 0.2} __css={styles.addProductWrapper}>
              <AddProduct />
            </StyledAddProductWrapper>
          )}
        </StyledProductImage>
        <StyledItemTextWrapper __css={styles.itemTextWrapper}>
          <StyledTitle textDecoration={isCompleted ? 'line-through' : 'none'} __css={styles.title} typographyVariant="body1">
            {itemTitle}
          </StyledTitle>
          {isCompleted && productTitle && (
            <ClampedText component="p" typographyVariant="label2" wordBreak="break-word" clampAt={2} disableTooltip>
              {productTitle}
            </ClampedText>
          )}
        </StyledItemTextWrapper>
      </Flex>
    </StyledItemRow>
  );
};

const ItemSkeleton = () => {
  return (
    <StyledItemRow __css={styles.itemRow}>
      <StyledCheckboxWrapper __css={styles.checkboxWrapper}>
        <SkeletonThumbnail height={pxToRem(24)} width={pxToRem(24)} variant="circle" />
      </StyledCheckboxWrapper>
      <Box flex={`0 0 ${pxToRem(48)}`}>
        <Skeleton width={pxToRem(48)} height={pxToRem(48)} marginRight={5} />
      </Box>
      <StyledItemTextWrapper __css={styles.itemTextWrapper}>
        <Skeleton width={pxToRem(120)} height={pxToRem(21)} />
      </StyledItemTextWrapper>
    </StyledItemRow>
  );
};

Item.Skeleton = ItemSkeleton;
